import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NbIconConfig } from '@nebular/theme';
import { Subscription, catchError, combineLatest, of } from 'rxjs';
import { TutorService, TutorSettingItem } from '../../../../services/tutor.service';
import {
  AI_SETTINGS_EMBEDDED_SECTION,
  AiSettingsComponent,
} from '../ai-settings/ai-settings.component';
import {
  AI_SETTINGS_SECTIONS,
  filterSectionsByAvailableSettings,
} from '../ai-settings/ai-settings.sections';

/** Die Abschnitte der KI-Konfiguration, die unter „Lernprozess" Reiter sind
 *  (Reihenfolge = Reiter-Reihenfolge). Der Reiter-Schlüssel ist der
 *  Abschnittsschlüssel — /tutors/:id/learning/<Abschnitt>. */
const LEARNING_SECTIONS: { key: string; icon: string | NbIconConfig }[] = [
  { key: 'didaktik', icon: 'compass-outline' },
  { key: 'dozent', icon: 'person-outline' },
  { key: 'functions', icon: 'browser-outline' },
  { key: 'learner_profile', icon: 'person-done-outline' },
  { key: 'curriculum', icon: { icon: 'curriculum', pack: 'al' } },
];

/** Vorlagen, die einen Dozent-Bot verknüpfen können — bei ihnen gibt es die
 *  Reiter „Dozent" und „Lern-Interface" auch ohne eigene Einstellungen
 *  (VIRTUAL_SECTIONS). Wie `AiSettingsComponent.LINKABLE_TEMPLATES`. */
const LINKABLE_TEMPLATES = new Set(['alpha_tutor', 'alpha_flexible']);

/** So lange wartet die Seite auf die Vorlage, dann entscheidet sie ohne. */
const TEMPLATE_WAIT_MS = 4000;

interface TutorLearningTab {
  key: string;
  titleKey: string;
  hintKey: string;
  icon: string | NbIconConfig;
  /** Injector, der der eingebetteten KI-Konfiguration ihren Abschnitt gibt. */
  injector: Injector;
}

/**
 * Agent › Lernprozess — Didaktik, Dozent, Lern-Interface, Lernerprofil und
 * Curriculum als Reiter einer Seite (/tutors/:id/learning/<Abschnitt>), nach
 * dem Muster von „Wissen" (TutorKnowledgeComponent). Jeder Reiter ist die
 * normale KI-Konfiguration mit festem Abschnitt (AI_SETTINGS_EMBEDDED_SECTION,
 * je Reiter ein eigener Injector).
 *
 * Welche Reiter es gibt, entscheidet — wie in der KI-Konfiguration — die
 * Vorlage: eine eigene, frische Abfrage der Einstellungen genau dieses
 * Agenten (nicht der geteilte `tutorSettings$`, der beim Wechsel noch den
 * vorigen Agenten hält) plus die Vorlage für „Dozent"/„Lern-Interface".
 */
@Component({
  selector: 'app-tutor-learning',
  templateUrl: './tutor-learning.component.html',
  styleUrl: './tutor-learning.component.scss',
})
export class TutorLearningComponent implements OnInit, OnDestroy {
  readonly aiSettings = AiSettingsComponent;
  tutorId = '';
  tabs: TutorLearningTab[] = [];
  ready = false;
  active = '';
  private wanted: string | null = null;
  private subs: Subscription[] = [];
  private fallbackTimer: ReturnType<typeof setTimeout> | undefined;
  /** Ein Injector je Abschnitt, einmal gebaut — sonst baute
   *  ngComponentOutlet die Einstellungen bei jeder Prüfung neu. */
  private readonly injectors = new Map<string, Injector>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private tutorService: TutorService,
    private injector: Injector
  ) {}

  ngOnInit(): void {
    this.tutorId = this.route.parent?.snapshot.paramMap.get('tutorId') ?? '';
    if (!this.tutorService.tutorTemplates$.value.length) {
      this.tutorService.listTutorTemplates().subscribe({ error: () => {} });
    }
    this.subs.push(
      this.route.paramMap.subscribe((params) => {
        this.wanted = params.get('tab');
        this.select();
      }),
      combineLatest([
        // Scheitert die Abfrage, gelten keine eigenen Einstellungen — es
        // bleiben die Reiter, die allein die Vorlage vorgibt.
        this.tutorService.fetchTutorSettings(this.tutorId).pipe(
          catchError(() => of([] as TutorSettingItem[]))
        ),
        this.tutorService.tutors$,
        this.tutorService.tutorTemplates$,
      ]).subscribe(([settings, , templates]) => {
        const tutor = this.tutorService.findTutor(this.tutorId);
        const template = templates.find((t) => t.id === tutor?.template_id);
        // Erst entscheiden, wenn die Vorlage feststeht — sonst fehlten beim
        // Direktlink auf „Dozent" kurz die virtuellen Reiter. Kommt sie nicht
        // (Vorlagen/Agent nicht ladbar), nach kurzer Zeit ohne sie — statt
        // eines endlosen Spinners.
        if (!template) {
          this.fallbackTimer ??= setTimeout(() => {
            if (!this.ready) this.decide(settings || [], false);
          }, TEMPLATE_WAIT_MS);
          return;
        }
        this.decide(settings || [], LINKABLE_TEMPLATES.has(template.name));
      })
    );
  }

  private decide(settings: TutorSettingItem[], isLinkableTutor: boolean): void {
    const available = new Set(
      filterSectionsByAvailableSettings(settings, { isLinkableTutor }).map((s) => s.key)
    );
    const tabs = LEARNING_SECTIONS.filter((s) => available.has(s.key)).map((s) => this.tabFor(s.key, s.icon));
    // Same instances while the set is unchanged — no tab rebuild.
    if (tabs.map((t) => t.key).join() !== this.tabs.map((t) => t.key).join()) this.tabs = tabs;
    this.ready = true;
    this.select();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    if (this.fallbackTimer) clearTimeout(this.fallbackTimer);
  }

  private tabFor(key: string, icon: string | NbIconConfig): TutorLearningTab {
    const section = AI_SETTINGS_SECTIONS.find((s) => s.key === key)!;
    let injector = this.injectors.get(key);
    if (!injector) {
      injector = Injector.create({
        providers: [{ provide: AI_SETTINGS_EMBEDDED_SECTION, useValue: key }],
        parent: this.injector,
      });
      this.injectors.set(key, injector);
    }
    return {
      key,
      titleKey: section.labelKey,
      hintKey: `pages.tutors.learningTabs.${key}`,
      icon,
      injector,
    };
  }

  private select(): void {
    if (!this.ready || !this.tabs.length) return;
    const hit = this.tabs.find((t) => t.key === this.wanted);
    if (hit) {
      this.active = hit.key;
    } else {
      this.router.navigate(['/tutors', this.tutorId, 'learning', this.tabs[0].key], { replaceUrl: true });
    }
  }

  onTabChange(tab: { tabId: string }): void {
    if (tab?.tabId && tab.tabId !== this.active) {
      this.router.navigate(['/tutors', this.tutorId, 'learning', tab.tabId], { replaceUrl: true });
    }
  }

  trackTab = (_: number, t: TutorLearningTab) => t.key;
}
